import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { BsGrid3X3GapFill, BsFillEnvelopeFill } from "react-icons/bs";
import BellIconWithNotifications, {
  NotificationWidget,
} from "../notifications/notificationwidget";
import { IoMap } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";
import BackIcon from "../../../assets/images/back-icon.svg";
import { useNavigate, useLocation } from "react-router-dom";

const AdminRole = () => {
  const [userEmail, setUserEmail] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Get roleId from navigation state
  const roleId = location.state?.roleId;

  // State management
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [roleInfo, setRoleInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("");

  // Fetch users assigned to this role
  const fetchAssignedUsers = async () => {
    if (!roleId) return;

    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("accessToken");

      if (!token) {
        setError("Authentication token not found");
        return;
      }

      const response = await fetch(
        `${apiUrl}renewals/users?renewalRoleId=${roleId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAssignedUsers(data.data || []);

        // Set role info if available
        if (data.data.length > 0 && data.data[0].renewalRole) {
          setRoleInfo(data.data[0].renewalRole);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to fetch assigned users");
      }
    } catch (error) {
      console.error("Error fetching assigned users:", error);
      setError("Failed to fetch assigned users");
    } finally {
      setLoading(false);
    }
  };

  // Get role info if we don't have it from users
  const fetchRoleInfo = async () => {
    if (!roleId || roleInfo) return;

    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${apiUrl}renewals/roles`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const roles = data.data || [];
        const currentRole = roles.find((role) => role.id === roleId);

        if (currentRole) {
          setRoleInfo({
            id: currentRole.id,
            name: currentRole.role,
            isActive: currentRole.isActive,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching role info:", error);
    }
  };

  // Assign role to user
  const assignRoleToUser = async () => {
    if (!userEmail.trim()) {
      setError("Please enter a user email address.");
      return;
    }

    if (!roleId) {
      setError("Role ID not available");
      return;
    }

    try {
      setAssignLoading(true);
      setError("");
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${apiUrl}renewals/assign-role`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userEmail.trim(),
          renewalRoleId: roleId,
          assignedBy: userName || "system",
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Role assigned to ${userEmail} successfully!`);
        setUserEmail("");
        // Refresh the users list
        fetchAssignedUsers();
      } else {
        setError(data.message || "Failed to assign role");
      }
    } catch (error) {
      console.error("Error assigning role:", error);
      setError("Failed to assign role");
    } finally {
      setAssignLoading(false);
    }
  };

  // Remove role from user (revoke) - API not implemented yet
  /*
  const revokeRoleFromUser = async (userEmail, userName) => {
    if (!window.confirm(`Are you sure you want to revoke the role from ${userName}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${apiUrl}renewals/remove-role`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userEmail,
          removedBy: userName || "system"
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Role revoked from ${userName} successfully!`);
        // Refresh the users list
        fetchAssignedUsers();
      } else {
        setError(data.message || "Failed to revoke role");
      }
    } catch (error) {
      console.error("Error revoking role:", error);
      setError("Failed to revoke role");
    }
  };
  */

  // Load data on component mount
  useEffect(() => {
    // Get user info from localStorage
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.name) {
      setUserName(user.name);
    }

    // Check if roleId is provided
    if (!roleId) {
      setError("Role ID not provided. Redirecting...");
      setTimeout(() => navigate("/renewals-manage-roles"), 2000);
      return;
    }

    // Fetch assigned users and role info
    fetchAssignedUsers();
    fetchRoleInfo();
  }, [roleId]);

  const handleBack = () => {
    navigate("/renewals-manage-roles");
  };

  const handleEmailChange = (e) => {
    setUserEmail(e.target.value);
    setError(""); // Clear error when user types
  };

  const handleAssignRole = () => {
    assignRoleToUser();
  };

  const handleRevoke = (user) => {
    // TODO: Implement revoke functionality when API is available
    // revokeRoleFromUser(user.email, user.name);
    console.log("Revoke functionality not implemented yet");
  };

  if (!roleId) {
    return (
      <div className="content-body">
        <div className="alert alert-danger">
          Role ID not provided. Redirecting to manage roles...
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-title d-flex align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn p-0 me-3 text-dark"
            onClick={handleBack}
            style={{ fontSize: "1.5rem" }}
          >
            <img src={BackIcon} alt="Back" />
          </button>
          <h1 className="mb-0">
            {roleInfo ? `${roleInfo.name} Role` : "Role Management"}
          </h1>
        </div>
        <div className="d-flex align-items-center gap-3">
          <BellIconWithNotifications />
          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                className="rounded-circle border-0 bg-transparent"
              >
                <div
                  className="menu-icon"
                  style={{
                    fontSize: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsGrid3X3GapFill />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-custom">
                <Dropdown.Item
                  href="/dashboard"
                  className="dropdown-item-custom"
                >
                  <BsFillEnvelopeFill className="dropdown-icon" /> Quote/Bind
                  Email
                </Dropdown.Item>
                <Dropdown.Item
                  href="/geographical-concentration"
                  className="dropdown-item-custom"
                >
                  <IoMap className="dropdown-icon" /> Geographical Concentration
                </Dropdown.Item>
                <Dropdown.Item
                  href="/renewals-dashboard"
                  className="dropdown-item-custom"
                >
                  <FiRefreshCw className="dropdown-icon" /> Renewals
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button
            type="button"
            className="btn-close float-end"
            onClick={() => setError("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Role Info */}
      {roleInfo && (
        <div className="content-body mb-3">
          <div className="card border-0 bg-light">
            <div className="card-body">
              <h5 className="card-title mb-1">{roleInfo.name}</h5>
              <span
                className={`badge ${
                  roleInfo.isActive ? "bg-success" : "bg-secondary"
                }`}
              >
                {roleInfo.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Assign Role Section */}
      <div className="content-body mb-3">
        <div className="d-flex gap-3 align-items-center">
          <input
            type="email"
            className="form-control"
            placeholder="Enter User Email Address"
            value={userEmail}
            onChange={handleEmailChange}
            style={{ maxWidth: "400px" }}
            disabled={assignLoading}
          />
          <button
            className="btn text-white px-4"
            onClick={handleAssignRole}
            disabled={assignLoading || loading}
            style={{ backgroundColor: "#122066", border: "none" }}
          >
            {assignLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Assigning...
              </>
            ) : (
              "Assign Role"
            )}
          </button>
        </div>
      </div>

      {/* Assigned Users Section */}
      <div className="content-body mb-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="subtitle mb-0">Assigned Users</h4>
          <span className="badge bg-primary">
            {assignedUsers.length} user{assignedUsers.length !== 1 ? "s" : ""}{" "}
            assigned
          </span>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading assigned users...</p>
          </div>
        )}

        {/* Users Table */}
        {!loading && (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <th>Username</th>
                  <th>Email address</th>
                  <th>User Type</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {assignedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-muted">
                      No users assigned to this role yet.
                    </td>
                  </tr>
                ) : (
                  assignedUsers.map((user) => (
                    <tr key={`${user.userType}-${user.id}`}>
                      <td className="fw-medium">{user.name}</td>
                      <td className="text-muted">{user.email}</td>
                      <td>
                        <span
                          className={`badge ${
                            user.userType === "AdminUser"
                              ? "bg-warning"
                              : "bg-info"
                          }`}
                        >
                          {user.userType === "AdminUser" ? "Admin" : "User"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex justify-content-end">
                          {/* Revoke button disabled until API is implemented */}
                          <button
                            className="btn btn-secondary btn-sm"
                            disabled
                            title="Revoke functionality not available yet"
                          >
                            Revoke
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminRole;
